import { PropsWithChildren, useCallback, useMemo, useState } from 'react'
import { Rect } from '../../types/diagram.ts'
import { CustomizeDiagramViewPortContext, DiagramViewPortContext, ZoomMode } from './DiagramViewPortContext.tsx'

export const DiagramViewPortProvider = ({ children }: PropsWithChildren) => {
    const [viewPort, setViewPort] = useState<Rect>({ x: 0, y: 0, width: 0, height: 0 })
    const [initialViewPort, setInitialViewPort] = useState<Rect>({ x: 0, y: 0, width: 0, height: 0 })
    const [zoom, setZoom] = useState<number>(1)
    const [zoomMode, setZoomMode] = useState<ZoomMode>('none')
    const [container, setContainer] = useState<SVGSVGElement | undefined>(undefined)
    const [htmlContainer, setHTMLContainer] = useState<HTMLElement | undefined>(undefined)

    const resetViewPort = useCallback(() => {
        setViewPort(() => initialViewPort)
        setZoom(() => 1)
    }, [setViewPort, initialViewPort])

    const resetZoomOnClick = useCallback(() => {
        setZoomMode(() => 'none')
    }, [setZoomMode])

    const setZoomInOnClick = useCallback(() => {
        setZoomMode(() => 'in')
    }, [setZoomMode])

    const setZoomOutOnClick = useCallback(() => {
        setZoomMode(() => 'out')
    }, [setZoomMode])

    const diagramViewPortContextValue = useMemo(
        () => ({
            viewPort,
            initialViewPort,
            zoom,
            container,
            htmlContainer,
            zoomMode
        }),
        [viewPort, initialViewPort, zoom, container, htmlContainer, zoomMode]
    )

    const customizeDiagramViewPortContextValue = useMemo(
        () => ({
            setViewPort,
            setInitialViewPort,
            setZoom,
            setContainer,
            resetViewPort,
            resetZoomOnClick,
            setZoomInOnClick,
            setZoomOutOnClick,
            setHTMLContainer
        }),
        [
            setZoomOutOnClick, setZoomInOnClick, resetZoomOnClick,
            resetViewPort, setViewPort, setInitialViewPort, setZoom, setContainer]
    )

    return <DiagramViewPortContext.Provider value={diagramViewPortContextValue}>
        <CustomizeDiagramViewPortContext.Provider value={customizeDiagramViewPortContextValue}>
            {children}
        </CustomizeDiagramViewPortContext.Provider>
    </DiagramViewPortContext.Provider>
}
